import { useAnimals } from '@/api/hooks';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import {
  animalIsAvailableForLot,
  sortAnimalsForLotInclusion,
} from '@/lib/lotAnimalSelection';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

export function LotAnimalPicker({
  propertyId,
  selectedIds,
  onChange,
  optional = false,
}: {
  propertyId: number;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  /** Quando true, deixa claro que a seleção não é obrigatória. */
  optional?: boolean;
}) {
  const { data: animalsData, isLoading } = useAnimals({
    property_id: propertyId,
    page: 1,
    per_page: 200,
  });
  const [search, setSearch] = useState('');

  const animals = animalsData?.animals ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = !q
      ? animals
      : animals.filter((a) => {
          const hay = [a.internal_number, a.animal_name, a.lot_name, a.breed_abbreviation]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        });
    return sortAnimalsForLotInclusion(list);
  }, [animals, search]);

  const selectableFiltered = useMemo(
    () => filtered.filter(animalIsAvailableForLot),
    [filtered],
  );

  function toggleAnimal(id: number) {
    const animal = animals.find((a) => a.id === id);
    if (!animal || !animalIsAvailableForLot(animal)) return;
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  }

  function toggleAllFiltered() {
    const ids = selectableFiltered.map((a) => a.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    if (allSelected) {
      onChange(selectedIds.filter((id) => !ids.includes(id)));
    } else {
      onChange(Array.from(new Set([...selectedIds, ...ids])));
    }
  }

  const filteredAllSelected =
    selectableFiltered.length > 0 &&
    selectableFiltered.every((a) => selectedIds.includes(a.id));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-manejai-forest">
          Animais do lote{' '}
          <span className="font-normal text-manejai-bark/55">
            {optional ? '(opcional)' : ''}
            {selectedIds.length > 0
              ? ` · ${selectedIds.length} selecionado${selectedIds.length === 1 ? '' : 's'}`
              : ''}
          </span>
        </p>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={toggleAllFiltered}
          disabled={selectableFiltered.length === 0}
        >
          {filteredAllSelected ? 'Limpar filtrados' : 'Selecionar disponíveis'}
        </Button>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-manejai-bark/40" />
        <input
          className="w-full rounded-xl border border-manejai-sand bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-manejai-forest"
          placeholder="Buscar por número, nome ou lote…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="max-h-56 overflow-y-auto rounded-2xl border border-manejai-sand">
        {isLoading ? (
          <p className="px-3 py-6 text-center text-sm text-manejai-bark/55">Carregando animais…</p>
        ) : filtered.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-manejai-bark/55">
            Nenhum animal encontrado nesta propriedade.
          </p>
        ) : (
          <ul className="divide-y divide-manejai-sand/70">
            {filtered.map((animal) => {
              const available = animalIsAvailableForLot(animal);
              const checked = selectedIds.includes(animal.id);
              const label = [animal.internal_number, animal.animal_name]
                .filter(Boolean)
                .join(' · ');
              return (
                <li key={animal.id}>
                  <label
                    className={cn(
                      'flex items-start gap-3 px-3 py-2.5 text-sm transition-colors',
                      available
                        ? cn(
                            'cursor-pointer',
                            checked ? 'bg-manejai-forest/5' : 'hover:bg-manejai-cream/40',
                          )
                        : 'cursor-not-allowed bg-manejai-cream/25 opacity-70',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      disabled={!available}
                      onChange={() => toggleAnimal(animal.id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-manejai-forest">
                        {!available && animal.lot_name ? (
                          <>
                            <span className="text-manejai-bark/70">[{animal.lot_name}] </span>
                            {label}
                          </>
                        ) : (
                          label
                        )}
                      </span>
                      <span className="block text-xs text-manejai-bark/55">
                        {animal.sex === 'male' ? 'Macho' : 'Fêmea'}
                        {animal.breed_abbreviation ? ` · ${animal.breed_abbreviation}` : ''}
                        {available ? ' · disponível' : ' · já pertence a um lote'}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <p className="text-xs text-manejai-bark/50">
        {optional
          ? 'Você pode criar o lote vazio e associar animais depois. '
          : ''}
        Animais que já estão em outro lote aparecem no fim e não podem ser selecionados.
      </p>
    </div>
  );
}
